<script setup>
/**
 * ONE QUESTION, ONE SCREEN — the step that showed 26 controls and now shows five.
 *
 * ── THE INVERSION, AND WHY IT IS LOAD BEARING ─────────────────────────────────────────
 * The box at the top is the QUESTION, not the instruction. `helpers/task-question.js`
 * writes the instruction, the step's name and the save key from it, each under the legacy
 * rule — so `questionEn` gets written at all, instead of being one more chore nobody does.
 * It is not a convenience: the builder has never exposed that field, which is exactly why
 * `lang=ar` answers `flow: null` with every step marked `no_question_in_language` and an
 * Arabic customer gets no card.
 *
 * ── THE CARVE-OUT, LIFTED (P-10.24) ───────────────────────────────────────────────────
 * A `choose_live` carries a customer-facing question now: the chat sends a saved question
 * word for word, and a live list is asked in the chat like any other. It still never becomes
 * a card field (`task-arabic.js`). `questionFieldFor` asks `carriesQuestion`, which says so,
 * rather than remembering it.
 *
 * ── TYPE ONCE (P-7.1) ─────────────────────────────────────────────────────────────────
 * The question box and every option are ONE `BilingualBox` each: the words land in the field
 * of the language they are typed in (`questionAr` for Arabic, never `questionEn`), and the
 * other language is a ghost line with a priced Suggest. On a one-language account only that
 * language's box is shown.
 *
 * ── THE EARNING RULE ──────────────────────────────────────────────────────────────────
 * A control appears when the thing it configures has become real — not when you click
 * Advanced. The system picker is earned by choosing "from your system"; the wiring card by
 * choosing a system; the wiring SCREEN by pressing Set it up. At most one Advanced row, and
 * it holds exactly one control. A drawer is where 26 controls go to hide, and it is how
 * this product got here.
 *
 * Nothing here teleports. Every disclosure opens in place.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import AnswerKindPicker from './AnswerKindPicker.vue';
import BilingualBox from './BilingualBox.vue';
import TaskSaveTarget from '../TaskSaveTarget.vue';
import TaskStepLink from '../TaskStepLink.vue';
import TaskStepPreview from '../TaskStepPreview.vue';
import WasloInput from '../WasloInput.vue';
import WasloSelect from '../WasloSelect.vue';
import WasloTextArea from '../WasloTextArea.vue';
import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { rowForStep } from '../../helpers/task-answer-kinds.js';
import { MAX_QUESTION } from '../../helpers/task-arabic.js';
import {
  MAX_CHOOSE_OPTIONS,
  MAX_INSTRUCTION,
  MAX_OPTION_LABEL,
  PHONE_COUNTRY_CHOICES,
  connectionFedBy,
  toStepRow,
} from '../../helpers/task-flow-draft.js';
import { BUTTONS_UP_TO } from '../../helpers/menu-draft.js';
import {
  CARRY_ON,
  goToStep,
  spineToDefinition,
} from '../../helpers/task-spine.js';
import {
  clampMany,
  manyBounds,
  optionCapOf,
  withBound,
} from '../../helpers/task-many.js';
import {
  MAX_KEPT_SUFFIX,
  MAX_READ_BACK_KEEP,
  keepsKey,
  readBackKeepChoices,
  readBackTargetOf,
  shownInReadBack,
  withKeptWords,
  withReadBackKept,
} from '../../helpers/task-read-back.js';
import {
  blankOption,
  needTextOf,
  withAnswerRow,
  withNeedText,
} from '../../helpers/task-spine-edit.js';
import {
  instructionFor,
  nameFor,
  questionLangOf,
} from '../../helpers/task-question.js';
import { answerLeavesChat, optionSends } from '../../helpers/task-outward.js';
import { purposeOf } from '../../helpers/connection-draft.js';
import {
  fileChoices,
  isUploadStep,
  mbChoices,
  uploadLimitsOf,
  withUploadLimit,
} from '../../helpers/task-uploads.js';

const props = defineProps({
  /** A spine row whose step is an `ask`, a `choose` or a `choose_live`. */
  row: { type: Object, required: true },
  /** Every other row, for the "if nothing came back" picker. */
  rows: { type: Array, default: () => [] },
  /** Save keys the REST of the task writes, so a derived one cannot collide. */
  takenKeys: { type: Array, default: () => [] },
  /** `['name','email','phone_number']`, as Chatwoot spells them. */
  contactFields: { type: Array, default: () => [] },
  /** `[{name, description}]` or `null` when the list could not be read. */
  connections: { type: Array, default: null },
  /** What a real "Try it" produced for this step: `{labels, total, more}`. */
  live: {
    type: Object,
    default: () => ({ labels: [], total: 0, more: false }),
  },
  /** The account's "Your customers write in" (P-7.1): `ar`, `en` or `both`. */
  languages: { type: String, default: 'both' },
});

const emit = defineEmits(['update:row', 'wiring']);

/** A detail's short name in a read-back, as `schema.ts` caps it (`MAX_READ_BACK_LABEL`). */
const MAX_READ_BACK_LABEL = 40;

/** What a choice sends, as `schema.ts` caps it (`MAX_OPTION_VALUE`). */
const MAX_OPTION_VALUE = 100;

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.STEP.${name}`;

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

const step = computed(() => props.row.step ?? {});
const type = computed(() => step.value.type ?? 'ask');
const isLive = computed(() => type.value === 'choose_live');
const isChoose = computed(() => type.value === 'choose');

const answerRow = computed(() => rowForStep(step.value) ?? '');

const write = row => emit('update:row', row);
const patchStep = changes =>
  write({ ...props.row, step: { ...step.value, ...changes } });

// ── phone questions (P-10.10, O-16) ─────────────────────────────────────────────────
const isPhone = computed(
  () => type.value === 'ask' && step.value.kind === 'phone'
);

/**
 * Kuwait shows as chosen when the step names none: that is what the engine uses for a
 * Kuwait account, and the product's market. The first change writes the list out
 * explicitly, so from then on the screen and the engine read the same countries.
 */
const phoneCountries = computed(() =>
  Array.isArray(step.value.phoneCountries) && step.value.phoneCountries.length
    ? step.value.phoneCountries
    : ['KW']
);

/**
 * The last ticked country is locked rather than refused. A refusal after the fact is a
 * lie: the browser has already cleared the box, and `Checkbox` only re-renders when its
 * model changes, so the screen would show no country while the step still names one.
 * The guard in `toggleCountry` stays as the backstop, never as the mechanism.
 */
const isLastCountry = country =>
  phoneCountries.value.length === 1 && phoneCountries.value.includes(country);

const toggleCountry = (country, on) => {
  const current = phoneCountries.value;
  const next = on
    ? [...new Set([...current, country])]
    : current.filter(one => one !== country);
  if (next.length === 0) return;
  patchStep({
    phoneCountries: PHONE_COUNTRY_CHOICES.filter(one => next.includes(one)),
  });
};

const setLandlines = on => patchStep({ phoneLines: on ? 'any' : undefined });

// ── photo and file questions (services field fixes G2.4) ──────────────────────────────
const isUpload = computed(() => isUploadStep(step.value));
const uploadLimits = computed(() => uploadLimitsOf(step.value));
/** Only counts and sizes that stay inside Meta's 100 MB together: never one the save refuses. */
const uploadCountChoices = computed(() =>
  fileChoices(uploadLimits.value.maxMb).map(count => ({
    value: String(count),
    label: format.value.format(count),
  }))
);
const sizeChoices = computed(() =>
  mbChoices(uploadLimits.value.maxFiles).map(size => ({
    value: String(size),
    label: t(key('UPLOAD_SIZE_OPTION'), { size }),
  }))
);
const setUploadLimit = (field, value) =>
  write({
    ...props.row,
    step: withUploadLimit(step.value, { [field]: Number(value) }),
  });
/** A photo or file is links, never a contact's name, email or phone. */
const saveContactFields = computed(() =>
  isUpload.value ? [] : props.contactFields
);

// ── ❶ the one box ───────────────────────────────────────────────────────────────────
/** The language the question is written in first: what the box opens on. */
const questionLang = computed(() => questionLangOf(step.value));
/** The question as it is written first, the words the instruction is generated from. */
const said = computed(() => needTextOf(step.value, questionLang.value));
const hasQuestion = computed(
  () =>
    (step.value.questionEn ?? '').trim() !== '' ||
    (step.value.questionAr ?? '').trim() !== ''
);

const setSaid = (value, lang) =>
  write(withNeedText(props.row, value, props.takenKeys, lang));

/** Words moved to the other field: the emptied side first, so the instruction follows the words. */
const setPair = pair => {
  const emptied = pair.primary === 'ar' ? 'en' : 'ar';
  const cleared = withNeedText(
    props.row,
    pair[emptied],
    props.takenKeys,
    emptied
  );
  write(
    withNeedText(cleared, pair[pair.primary], props.takenKeys, pair.primary)
  );
};

/**
 * The owner's own instruction, still being used.
 *
 * Shown only when the stored instruction is NOT what the generator would write for the
 * question that is on screen — which is precisely the case the legacy rule protects. The
 * offer is a button; nothing here ever edits somebody's prose in place.
 */
const instructionIsOwn = computed(
  () =>
    // A live list whose box still shows its old instruction has written no question yet:
    // there is nothing to offer to replace (P-10.24).
    (!isLive.value || hasQuestion.value) &&
    said.value.trim() !== '' &&
    (step.value.instruction ?? '') !== instructionFor(type.value, said.value)
);

const useTheQuestion = () =>
  patchStep({
    instruction: instructionFor(type.value, said.value),
    name: nameFor(said.value),
  });

// ── how it is asked (P-10.24) ──────────────────────────────────────────────────────
/** Absent means on: a saved question goes out word for word. */
const asWritten = computed(() => step.value.asWritten !== false);
const setAsWritten = on => patchStep({ asWritten: on ? undefined : false });

// ── ❷ what comes back ───────────────────────────────────────────────────────────────
const setAnswerRow = id => write(withAnswerRow(props.row, id, props.takenKeys));

// ── the options an owner writes (row 7) ─────────────────────────────────────────────
const options = computed(() =>
  Array.isArray(props.row.options) ? props.row.options : []
);

const setOption = (index, changes) =>
  write({
    ...props.row,
    options: options.value.map((option, at) =>
      at === index
        ? { ...option, option: { ...option.option, ...changes } }
        : option
    ),
  });

const addOption = () =>
  write({
    ...props.row,
    options: [
      ...options.value,
      blankOption(options.value.map(option => option.option?.id)),
    ],
  });

/**
 * What each choice sends, earned (P-2.3): the box appears only when this answer leaves the
 * chat, saved on the customer or sent to a system. Every other step, the value is invisible
 * and nobody should have to think about it.
 */
const leavesChat = computed(() =>
  answerLeavesChat(step.value, [
    ...props.rows.map(row => row.step).filter(Boolean),
    step.value,
  ])
);
const sendsHint = option =>
  optionSends({ ...option.option, value: '' }) || t(key('OPTION_VALUE_HINT'));

const removeOption = index => {
  const left = options.value.filter((_, at) => at !== index);
  // G2.3: a "how many" past the choices that are left is pulled back with them.
  const many = step.value.many;
  write({
    ...props.row,
    options: left,
    ...(many
      ? { step: { ...step.value, many: clampMany(many, left.length) } }
      : {}),
  });
};

// ── more than one answer (field fixes G2.3) ─────────────────────────────────────────
/** They may pick more than one of these choices. */
const isMany = computed(() => isChoose.value && step.value.many !== undefined);
/** A choice with a step of its own decides where the task goes; a set of answers cannot. */
const branchesOwn = computed(() =>
  options.value.some(option => option.then?.go === 'step')
);
/** Past ten choices, one answer would be a question WhatsApp cannot show: turning it off waits. */
const manyLocked = computed(
  () => isMany.value && options.value.length > MAX_CHOOSE_OPTIONS
);
const optionCap = computed(() => optionCapOf(step.value.many));
const setMany = on => {
  if (!on) {
    patchStep({ many: undefined });
    return;
  }
  // Every choice carries on, and nothing is read back: a set picks no single way on.
  write({
    ...props.row,
    options: options.value.map(option => ({ ...option, then: CARRY_ON })),
    step: {
      ...step.value,
      many: {},
      readBackOf: undefined,
      readBackKeep: undefined,
    },
  });
};
const bounds = computed(() =>
  manyBounds(step.value.many, options.value.length)
);
const countChoices = computed(() =>
  options.value.map((_, at) => ({
    value: String(at + 1),
    label: format.value.format(at + 1),
  }))
);
const setBound = (bound, value) =>
  patchStep({
    many: withBound(
      step.value.many,
      bound,
      Number(value),
      options.value.length
    ),
  });

/**
 * How WhatsApp will draw them — a STATEMENT, never a choice.
 *
 * `menus/render-choices.ts` is the one place that decides it, from the count alone, so
 * buttons-versus-list is a consequence and the owner is told it rather than asked. The
 * character cap says 20 whichever shape it takes, because a task step's option label is
 * `MAX_OPTION_LABEL` in `schema.ts` regardless — a menu row may go to 24, a step may not.
 */
const shapeNote = computed(() => {
  const count = options.value.length;
  if (count === 0) return '';
  // G2.3: more than one answer is always the numbered list.
  let shape = count <= BUTTONS_UP_TO ? 'SHAPE_BUTTONS' : 'SHAPE_LIST';
  if (isMany.value) shape = 'SHAPE_MANY';
  return t(key(shape), {
    count: format.value.format(count),
    limit: format.value.format(MAX_OPTION_LABEL),
  });
});

// ── ❸ the system, earned by row 8 ───────────────────────────────────────────────────
const connectionChoices = computed(() =>
  (props.connections ?? []).map(row => ({
    value: row.name,
    label: row.description ? `${row.name}: ${row.description}` : row.name,
  }))
);

const hasConnection = computed(() => (step.value.connection ?? '') !== '');

/** What the wiring screen has already been told, as one sentence each. */
const sendsNote = computed(() => {
  const inputs = Object.entries(step.value.inputs ?? {});
  if (inputs.length === 0) return t(key('SENDS_NONE'));
  return t(key('SENDS'), { count: format.value.format(inputs.length) });
});

// ── G1: a live list on the form ─────────────────────────────────────────────────────
/** The connection this list runs, as the account's list holds it. */
const liveConnection = computed(
  () =>
    (props.connections ?? []).find(row => row.name === step.value.connection) ??
    null
);
/**
 * Only a look-up may fill a form page: anything that changes her system still runs in the
 * chat, after the customer says yes. Earned, like every control here: it appears once the
 * list has a look-up behind it.
 */
const canShowOnForm = computed(
  () => isLive.value && purposeOf(liveConnection.value) === 'lookup'
);
const onForm = computed(() => step.value.onForm === true);
const setOnForm = on => patchStep({ onForm: on ? true : undefined });

const getsNote = computed(() => {
  if ((step.value.listPath ?? '') === '') return t(key('GETS_NOTHING'));
  return t(key('GETS'), { field: step.value.labelField || '—' });
});

// ── the read-back (P-10.2) ──────────────────────────────────────────────────────────
/** The task as it would be saved, with this row as it is on screen. */
const definition = computed(() => {
  const others = props.rows.filter(row => row.step?.id !== step.value.id);
  const at = props.rows.findIndex(row => row.step?.id === step.value.id);
  const rows = [...others];
  rows.splice(at < 0 ? rows.length : at, 0, props.row);
  return spineToDefinition({ rows });
});

/** The Call a yes here runs straight away, or `''`: only then is a read-back offered. */
const readBackTarget = computed(() =>
  // G2.3: a question with more than one answer confirms nothing, so it reads nothing back.
  isChoose.value && step.value.many === undefined
    ? readBackTargetOf(definition.value, step.value.id)
    : ''
);
const readsBack = computed(
  () =>
    readBackTarget.value !== '' &&
    step.value.readBackOf === readBackTarget.value
);
const setReadsBack = on =>
  write({
    ...props.row,
    readBackTouched: true,
    step: {
      ...step.value,
      readBackOf: on ? readBackTarget.value : undefined,
      // Services task review E: what it showed from earlier Calls goes with it, or the
      // sidecar refuses the step (`keep_without_read_back`) on a screen with no box to fix.
      ...(on ? {} : { readBackKeep: undefined }),
    },
  });

/**
 * Services task review E: what an EARLIER Call kept (a quote's price), shown under the
 * read-back with its own name in each language. Earned: offered only once the read-back is on
 * and some other Call of the task keeps something.
 */
const readBackKeep = computed(() =>
  Array.isArray(step.value.readBackKeep) ? step.value.readBackKeep : []
);
const keepChoices = computed(() =>
  readsBack.value ? readBackKeepChoices(definition.value, step.value.id) : []
);
const keepFull = computed(
  () => readBackKeep.value.length >= MAX_READ_BACK_KEEP
);
const toggleReadBackKept = (name, on) =>
  patchStep({ readBackKeep: withReadBackKept(readBackKeep.value, name, on) });
const setKeptWords = (name, field, value) =>
  patchStep({
    readBackKeep: withKeptWords(readBackKeep.value, name, field, value),
  });
const keptEntry = name =>
  readBackKeep.value.find(entry => entry?.key === name) ?? {};

/**
 * A NEW yes/no that confirms a change reads it back unless the owner says no. A saved one is
 * left exactly as it was saved: switching on what an owner never asked for is a rewrite.
 */
watch(
  readBackTarget,
  target => {
    if (
      target !== '' &&
      props.row.fresh === true &&
      props.row.readBackTouched !== true &&
      step.value.readBackOf === undefined
    ) {
      patchStep({ readBackOf: target });
    }
  },
  { immediate: true }
);

/**
 * G2.3: not offered on a question whose choices lead to steps of their own (`many_branches`).
 *
 * A question that leads on to a Call IS offered, though `readBackTarget` names that Call for
 * every choose that reaches one through messages, and the fresh-row watch below sets
 * `readBackOf` there. The server refuses only the `readBackOf` itself, and `setMany` removes
 * it; once `many` is on, `readBackTarget` is `''`, so the watch does not put it back.
 */
const manyBlocked = computed(() => !isMany.value && branchesOwn.value);

/** "Name in the summary": only when this answer is a line in a read-back or a summary. */
const inSummary = computed(() =>
  shownInReadBack(definition.value, step.value.saveTo?.chat ?? '')
);
const summaryName = computed(
  () => (step.value.readBackEn ?? '').trim() || step.value.name || ''
);
const setReadBackName = (field, value) =>
  patchStep({ [field]: value.trim() === '' ? undefined : value });

// ── the disclosure rows ─────────────────────────────────────────────────────────────
const open = ref('');
const toggle = name => {
  open.value = open.value === name ? '' : name;
};

const stepChoices = computed(() =>
  props.rows.map(row => ({
    id: row.step?.id,
    name:
      (row.step?.questionEn ?? '').trim() ||
      (row.step?.name ?? '').trim() ||
      row.step?.id,
    type: row.step?.type,
  }))
);

const ifNoneId = computed(() =>
  props.row.ifNone?.go === 'step' ? props.row.ifNone.step : ''
);

const setIfNone = value =>
  write({ ...props.row, ifNone: value ? goToStep(value) : undefined });

// ── if they can't answer (P-10.25), and optional (field report F1.3) ──────────────────
const STUCK_ACTIONS = ['handoff', 'skip', 'goto'];
/** F1.3: an optional question is skipped when they cannot answer, unless the owner chose otherwise. */
const isOptional = computed(() => step.value.optional === true);
const stuckAction = computed(
  () => step.value.ifStuck?.action ?? (isOptional.value ? 'skip' : 'handoff')
);
/**
 * T-16: the connection this answer is sent to, or `''`. Skipping it would send that system an
 * empty answer, so the sidecar refuses both "Skip this question" and "Optional" here
 * (`skip_feeds_connection`). Said on the screen, before the save is refused.
 */
const fedBy = computed(() =>
  connectionFedBy(
    { steps: props.rows.map(row => row.step).filter(Boolean) },
    step.value
  )
);
const setOptional = on => {
  if (!on) {
    patchStep({ optional: undefined });
    return;
  }
  // An explicit "skip" says nothing an optional question does not already say.
  patchStep({
    optional: true,
    ...(step.value.ifStuck?.action === 'skip' ? { ifStuck: undefined } : {}),
  });
};
const stuckNext = computed(() => step.value.ifStuck?.next ?? '');
/** "Ask something else instead" needs somewhere to go: the first other step, until she picks. */
const firstOther = computed(
  () =>
    stepChoices.value.find(row => row.id && row.id !== step.value.id)?.id ?? ''
);
const setStuck = action => {
  // On an optional question "skip" is what absent means, and "pass it on" has to be written.
  if (action === 'handoff') {
    patchStep({
      ifStuck: isOptional.value ? { action: 'handoff' } : undefined,
    });
  } else if (action === 'skip') {
    patchStep({ ifStuck: isOptional.value ? undefined : { action: 'skip' } });
  } else if (firstOther.value || stuckNext.value) {
    patchStep({
      ifStuck: { action: 'goto', next: stuckNext.value || firstOther.value },
    });
  }
};
/** A way out that would skip an answer the owner's system is sent cannot be chosen (T-16). */
const stuckDisabled = action =>
  (action === 'goto' && !firstOther.value && !stuckNext.value) ||
  (action === 'skip' && fedBy.value !== '' && stuckAction.value !== 'skip');
const setStuckNext = id => {
  if (id) patchStep({ ifStuck: { action: 'goto', next: id } });
};
const stuckName = computed(() => {
  if (stuckAction.value === 'skip') return t(key('IFSTUCK_SKIP'));
  if (stuckAction.value !== 'goto') return t(key('IFSTUCK_HANDOFF'));
  const found = stepChoices.value.find(row => row.id === stuckNext.value);
  return found ? found.name : stuckNext.value;
});

const ifNoneName = computed(() => {
  if (!ifNoneId.value) return t(key('IFNONE_HANDOVER'));
  const found = stepChoices.value.find(row => row.id === ifNoneId.value);
  return found ? found.name : ifNoneId.value;
});

const saveTo = computed(() => ({
  chat: step.value.saveTo?.chat ?? '',
  contact: step.value.saveTo?.contact ?? '',
}));

const setSaveTo = value => {
  const next = {};
  if ((value.chat ?? '').trim() !== '') next.chat = value.chat.trim();
  if ((value.contact ?? '').trim() !== '') next.contact = value.contact.trim();
  patchStep({ saveTo: Object.keys(next).length ? next : undefined });
};

const savedNote = computed(() => {
  const chat = saveTo.value.chat;
  const contact = saveTo.value.contact;
  if (!chat && !contact) return t(key('SAVED_NOWHERE'));
  if (contact)
    return t(key('SAVED_BOTH'), { name: chat || contact, field: contact });
  return t(key('SAVED_AS'), { name: chat });
});

// ── the preview, fed by the real renderer ───────────────────────────────────────────
const previewStep = computed(() =>
  toStepRow({
    ...step.value,
    options: options.value.map(option => option.option),
  })
);
</script>

<template>
  <div class="flex flex-col gap-6" data-waslo-question-step>
    <!-- ❶ -->
    <div class="flex flex-col gap-2">
      <BilingualBox
        :en="needTextOf(step, 'en')"
        :ar="needTextOf(step, 'ar')"
        :label="t(key(isLive ? 'NEED_LIVE' : 'NEED'))"
        :hint="t(key('NEED_HINT'))"
        :placeholder-en="t(key('NEED_PLACEHOLDER'))"
        :placeholder-ar="t(key('NEED_PLACEHOLDER'))"
        :max-length="MAX_QUESTION"
        multiline
        :writing="questionLang"
        :kind="hasQuestion ? 'task_question' : ''"
        :languages="languages"
        data-waslo-step-need
        @update:en="value => setSaid(value, 'en')"
        @update:ar="value => setSaid(value, 'ar')"
        @update:pair="setPair"
      />
      <p class="mb-0 text-sm text-n-slate-11">
        {{
          t(
            key(
              asWritten
                ? 'AS_WRITTEN_HINT'
                : isLive
                  ? 'NEED_NOTE_LIVE'
                  : 'NEED_NOTE'
            )
          )
        }}
      </p>

      <!-- The legacy rule, on screen: propose, never edit in place. -->
      <div
        v-if="instructionIsOwn"
        class="flex flex-col items-start gap-2 p-3 rounded-lg outline outline-1 outline-n-weak bg-n-alpha-1"
        data-waslo-step-own-wording
      >
        <p class="mb-0 text-sm text-n-slate-11">{{ t(key('OWN_WORDING')) }}</p>
        <Button
          :label="t(key('OWN_WORDING_REPLACE'))"
          variant="faded"
          color="slate"
          size="sm"
          data-waslo-step-own-wording-replace
          @click="useTheQuestion"
        />
      </div>
    </div>

    <!-- ❷ -->
    <AnswerKindPicker
      :model-value="answerRow"
      @update:model-value="setAnswerRow"
    />

    <!-- A phone question: which numbers it accepts. Kuwait mobiles unless told more. -->
    <div v-if="isPhone" class="flex flex-col gap-3" data-waslo-step-phone>
      <label
        class="flex items-start gap-2 text-sm cursor-pointer text-n-slate-12"
        data-waslo-step-phone-landlines
      >
        <Checkbox
          class="mt-0.5 shrink-0"
          :model-value="step.phoneLines === 'any'"
          @update:model-value="setLandlines"
        />
        <span class="flex flex-col gap-1">
          <span>{{ t(key('PHONE_LANDLINES')) }}</span>
          <span class="text-n-slate-11">{{
            t(key('PHONE_LANDLINES_HINT'))
          }}</span>
        </span>
      </label>
      <fieldset class="flex flex-col gap-2">
        <legend class="mb-1 text-sm font-medium text-n-slate-12">
          {{ t(key('PHONE_COUNTRIES')) }}
        </legend>
        <label
          v-for="country in PHONE_COUNTRY_CHOICES"
          :key="country"
          class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
          :data-waslo-step-phone-country="country"
        >
          <Checkbox
            class="shrink-0"
            :model-value="phoneCountries.includes(country)"
            :disabled="isLastCountry(country)"
            @update:model-value="on => toggleCountry(country, on)"
          />
          <span>{{ t(key(`PHONE_COUNTRY_${country.toUpperCase()}`)) }}</span>
        </label>
      </fieldset>
    </div>

    <!-- Services G2.4: how many photos or files, and how large. -->
    <div v-if="isUpload" class="flex flex-col gap-3" data-waslo-step-uploads>
      <div class="grid grid-cols-2 gap-3">
        <WasloSelect
          :model-value="String(uploadLimits.maxFiles)"
          :label="t(key('UPLOAD_COUNT'))"
          :options="uploadCountChoices"
          data-waslo-step-upload-count
          @update:model-value="value => setUploadLimit('maxFiles', value)"
        />
        <WasloSelect
          :model-value="String(uploadLimits.maxMb)"
          :label="t(key('UPLOAD_SIZE'))"
          :options="sizeChoices"
          data-waslo-step-upload-size
          @update:model-value="value => setUploadLimit('maxMb', value)"
        />
      </div>
      <p class="mb-0 text-sm text-n-slate-11" data-waslo-step-upload-note>
        {{ t(key('UPLOAD_NOTE')) }}
      </p>
    </div>

    <!-- Row 7 — the choices an owner writes, both languages on the row. -->
    <div v-if="isChoose" class="flex flex-col gap-3" data-waslo-step-options>
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('OPTIONS')) }}
      </p>
      <div
        v-for="(option, index) in options"
        :key="option.option?.id || index"
        class="flex items-end gap-3"
        data-waslo-step-option
      >
        <div class="grid flex-1 min-w-0 gap-3">
          <BilingualBox
            :en="option.option?.labelEn ?? ''"
            :ar="option.option?.labelAr ?? ''"
            :label="
              t(key('OPTION_LABEL'), { number: format.format(index + 1) })
            "
            :max-length="MAX_OPTION_LABEL"
            kind="task_option_label"
            :languages="languages"
            :data-waslo-step-option-label="option.option?.id"
            @update:en="value => setOption(index, { labelEn: value })"
            @update:ar="value => setOption(index, { labelAr: value })"
            @update:pair="
              pair => setOption(index, { labelEn: pair.en, labelAr: pair.ar })
            "
          />
          <WasloInput
            v-if="leavesChat"
            :model-value="option.option?.value ?? ''"
            :label="t(key('OPTION_VALUE'))"
            :hint="t(key('OPTION_VALUE_HINT'))"
            :placeholder="sendsHint(option)"
            :message="
              (option.option?.value ?? '').length > MAX_OPTION_VALUE
                ? t(key('OPTION_LONG'), {
                    limit: format.format(MAX_OPTION_VALUE),
                  })
                : ''
            "
            message-type="error"
            :data-waslo-step-option-value="option.option?.id"
            @update:model-value="value => setOption(index, { value })"
          />
        </div>
        <button
          type="button"
          class="grid mb-1 rounded-md place-items-center size-8 text-n-slate-11 hover:text-n-ruby-9 hover:bg-n-alpha-2"
          :aria-label="t(key('OPTION_REMOVE'))"
          :title="t(key('OPTION_REMOVE'))"
          :data-waslo-step-option-remove="option.option?.id"
          @click="removeOption(index)"
        >
          <span class="size-4 i-lucide-x" aria-hidden="true" />
        </button>
      </div>
      <div class="flex">
        <Button
          v-if="options.length < optionCap"
          :label="t(key('OPTION_ADD'))"
          icon="i-lucide-plus"
          variant="faded"
          color="slate"
          size="sm"
          data-waslo-step-option-add
          @click="addOption"
        />
      </div>
      <p
        v-if="shapeNote"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-step-shape
      >
        {{ shapeNote }}
      </p>
      <!-- Field fixes G2.3: more than one answer. -->
      <label
        class="flex items-start gap-2 text-sm cursor-pointer text-n-slate-12"
        data-waslo-step-many
      >
        <Checkbox
          class="mt-0.5 shrink-0"
          :model-value="isMany"
          :disabled="manyBlocked || manyLocked"
          @update:model-value="setMany"
        />
        <span class="flex flex-col gap-1">
          <span>{{ t(key('MANY')) }}</span>
          <span class="text-n-slate-11">{{ t(key('MANY_HINT')) }}</span>
        </span>
      </label>
      <p
        v-if="manyBlocked"
        class="mb-0 text-xs text-n-slate-11"
        data-waslo-step-many-blocked
      >
        {{ t(key('MANY_BLOCKED')) }}
      </p>
      <p
        v-if="manyLocked"
        class="mb-0 text-xs text-n-slate-11"
        data-waslo-step-many-locked
      >
        {{
          t(key('MANY_LOCKED'), { limit: format.format(MAX_CHOOSE_OPTIONS) })
        }}
      </p>
      <div
        v-if="isMany"
        class="grid grid-cols-2 gap-3"
        data-waslo-step-many-range
      >
        <WasloSelect
          :model-value="String(bounds.min)"
          :label="t(key('MANY_MIN'))"
          :options="countChoices"
          data-waslo-step-many-min
          @update:model-value="value => setBound('min', value)"
        />
        <WasloSelect
          :model-value="String(bounds.max)"
          :label="t(key('MANY_MAX'))"
          :options="countChoices"
          data-waslo-step-many-max
          @update:model-value="value => setBound('max', value)"
        />
      </div>
    </div>

    <!-- ❸ and ❹ — both earned. -->
    <div v-if="isLive" class="flex flex-col gap-3">
      <WasloSelect
        :model-value="step.connection ?? ''"
        :label="t(key('SYSTEM'))"
        :options="connectionChoices"
        :placeholder="t(key('SYSTEM_PLACEHOLDER'))"
        :message="connections === null ? t(key('SYSTEM_UNAVAILABLE')) : ''"
        message-type="error"
        data-waslo-step-system
        @update:model-value="value => patchStep({ connection: value })"
      />
      <p
        v-if="(connections ?? []).length === 0 && connections !== null"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-step-no-systems
      >
        {{ t(key('SYSTEM_NONE')) }}
      </p>

      <div
        v-if="hasConnection"
        class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
        data-waslo-step-wiring-card
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t(key('WIRING')) }}
          </p>
          <Button
            :label="t(key('WIRING_OPEN'))"
            icon="i-lucide-settings-2"
            variant="faded"
            color="slate"
            size="sm"
            data-waslo-step-wiring-open
            @click="emit('wiring')"
          />
        </div>
        <p class="mb-0 text-sm text-n-slate-11">{{ sendsNote }}</p>
        <p class="mb-0 text-sm text-n-slate-11">{{ getsNote }}</p>
      </div>

      <!-- G1: the form fetches this list live, from a look-up only. -->
      <label
        v-if="canShowOnForm"
        class="flex items-start gap-3 cursor-pointer"
        data-waslo-step-on-form
      >
        <Switch
          class="mt-0.5"
          :model-value="onForm"
          data-waslo-step-on-form-toggle
          @update:model-value="setOnForm"
        />
        <span class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-12">{{ t(key('ON_FORM')) }}</span>
          <span class="text-n-slate-11">{{ t(key('ON_FORM_HINT')) }}</span>
        </span>
      </label>
    </div>

    <!-- The read-back (P-10.2): Waslo writes it from what the Call sends, never the model. -->
    <label
      v-if="readBackTarget"
      class="flex items-start gap-3 cursor-pointer"
      data-waslo-step-read-back
    >
      <Switch
        class="mt-0.5"
        :model-value="readsBack"
        data-waslo-step-read-back-toggle
        @update:model-value="setReadsBack"
      />
      <span class="flex flex-col gap-1 text-sm">
        <span class="text-n-slate-12">{{ t(key('READ_BACK')) }}</span>
        <span class="text-n-slate-11">{{ t(key('READ_BACK_HINT')) }}</span>
      </span>
    </label>
    <fieldset
      v-if="keepChoices.length"
      class="flex flex-col gap-2 px-3"
      data-waslo-step-read-back-keep
    >
      <legend class="mb-1 text-sm text-n-slate-12">
        {{ t(key('READ_BACK_KEEP')) }}
      </legend>
      <p class="text-sm text-n-slate-11">
        {{
          t(key('READ_BACK_KEEP_HINT'), {
            limit: format.format(MAX_READ_BACK_KEEP),
          })
        }}
      </p>
      <div v-for="name in keepChoices" :key="name" class="flex flex-col gap-2">
        <label
          class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
        >
          <Checkbox
            :model-value="keepsKey(readBackKeep, name)"
            :disabled="keepFull && !keepsKey(readBackKeep, name)"
            :data-waslo-step-read-back-kept="name"
            @update:model-value="on => toggleReadBackKept(name, on)"
          />
          <bdi>{{ name }}</bdi>
        </label>
        <div
          v-if="keepsKey(readBackKeep, name)"
          class="grid grid-cols-1 gap-2 ps-6 sm:grid-cols-2"
          :data-waslo-step-read-back-kept-words="name"
        >
          <WasloInput
            :model-value="keptEntry(name).labelEn ?? ''"
            :label="t(key('READ_BACK_KEEP_NAME_EN'))"
            :max-length="MAX_READ_BACK_LABEL"
            data-waslo-kept-label-en
            @update:model-value="value => setKeptWords(name, 'labelEn', value)"
          />
          <WasloInput
            :model-value="keptEntry(name).labelAr ?? ''"
            :label="t(key('READ_BACK_KEEP_NAME_AR'))"
            :max-length="MAX_READ_BACK_LABEL"
            dir="auto"
            data-waslo-kept-label-ar
            @update:model-value="value => setKeptWords(name, 'labelAr', value)"
          />
          <WasloInput
            :model-value="keptEntry(name).suffixEn ?? ''"
            :label="t(key('READ_BACK_KEEP_SUFFIX_EN'))"
            :max-length="MAX_KEPT_SUFFIX"
            data-waslo-kept-suffix-en
            @update:model-value="value => setKeptWords(name, 'suffixEn', value)"
          />
          <WasloInput
            :model-value="keptEntry(name).suffixAr ?? ''"
            :label="t(key('READ_BACK_KEEP_SUFFIX_AR'))"
            :max-length="MAX_KEPT_SUFFIX"
            dir="auto"
            data-waslo-kept-suffix-ar
            @update:model-value="value => setKeptWords(name, 'suffixAr', value)"
          />
        </div>
      </div>
    </fieldset>

    <!-- What the phone shows. The real renderer, never a second mock of WhatsApp's rules. -->
    <TaskStepPreview
      v-if="isChoose || isLive"
      :step="previewStep"
      :live-labels="live.labels"
      :live-total="live.total"
      :live-more="live.more"
    />

    <!-- The rows that open in place. Never more than one Advanced, holding one control. -->
    <div class="flex flex-col gap-2">
      <template v-if="isLive">
        <button
          type="button"
          class="flex items-center w-full gap-2 px-3 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
          :aria-expanded="open === 'ifnone'"
          data-waslo-step-ifnone-toggle
          @click="toggle('ifnone')"
        >
          <span
            class="shrink-0 size-4 text-n-slate-11"
            :class="
              open === 'ifnone'
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
            aria-hidden="true"
          />
          <span class="flex-1 min-w-0">{{ t(key('IFNONE')) }}</span>
          <span class="text-n-slate-11"
            ><bdi>{{ ifNoneName }}</bdi></span
          >
        </button>
        <div v-if="open === 'ifnone'" class="px-3 pb-2">
          <TaskStepLink
            :model-value="ifNoneId"
            :label="t(key('IFNONE_LABEL'))"
            :steps="stepChoices"
            :exclude="step.id"
            :empty-label="t(key('IFNONE_HANDOVER'))"
            data-waslo-step-ifnone
            @update:model-value="setIfNone"
          />
        </div>
      </template>

      <button
        type="button"
        class="flex items-center w-full gap-2 px-3 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
        :aria-expanded="open === 'saved'"
        data-waslo-step-saved-toggle
        @click="toggle('saved')"
      >
        <span
          class="shrink-0 size-4 text-n-slate-11"
          :class="
            open === 'saved'
              ? 'i-lucide-chevron-down'
              : 'i-lucide-chevron-right'
          "
          aria-hidden="true"
        />
        <span class="flex-1 min-w-0"
          ><bdi>{{ savedNote }}</bdi></span
        >
      </button>
      <div v-if="open === 'saved'" class="px-3 pb-2">
        <TaskSaveTarget
          :model-value="saveTo"
          :contact-fields="saveContactFields"
          @update:model-value="setSaveTo"
        />
      </div>

      <!-- How it is asked (P-10.24). Opens in place; the box is not on the screen until then. -->
      <button
        type="button"
        class="flex items-center w-full gap-2 px-3 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
        :aria-expanded="open === 'asked'"
        data-waslo-step-asked-toggle
        @click="toggle('asked')"
      >
        <span
          class="shrink-0 size-4 text-n-slate-11"
          :class="
            open === 'asked'
              ? 'i-lucide-chevron-down'
              : 'i-lucide-chevron-right'
          "
          aria-hidden="true"
        />
        <span class="flex-1 min-w-0">{{ t(key('ASKED')) }}</span>
        <span class="text-n-slate-11">{{
          t(key(asWritten ? 'AS_WRITTEN' : 'AS_WRITTEN_OFF'))
        }}</span>
      </button>
      <div
        v-if="open === 'asked'"
        class="flex flex-col gap-3 px-3 pb-2"
        data-waslo-step-asked
      >
        <label
          class="flex items-start gap-2 text-sm cursor-pointer text-n-slate-12"
          data-waslo-step-as-written
        >
          <Checkbox
            class="mt-0.5 shrink-0"
            :model-value="asWritten"
            @update:model-value="setAsWritten"
          />
          <span class="flex flex-col gap-1">
            <span>{{ t(key('AS_WRITTEN')) }}</span>
            <span class="text-n-slate-11">{{
              t(key(asWritten ? 'AS_WRITTEN_HINT' : 'AS_WRITTEN_OFF'))
            }}</span>
          </span>
        </label>
      </div>

      <!-- Name in the summary (P-10.2). Only when this answer is shown back to the customer. -->
      <template v-if="inSummary">
        <button
          type="button"
          class="flex items-center w-full gap-2 px-3 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
          :aria-expanded="open === 'summary'"
          data-waslo-step-summary-name-toggle
          @click="toggle('summary')"
        >
          <span
            class="shrink-0 size-4 text-n-slate-11"
            :class="
              open === 'summary'
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
            aria-hidden="true"
          />
          <span class="flex-1 min-w-0">{{ t(key('SUMMARY_NAME')) }}</span>
          <span class="text-n-slate-11"
            ><bdi>{{ summaryName }}</bdi></span
          >
        </button>
        <div
          v-if="open === 'summary'"
          class="flex flex-col gap-3 px-3 pb-2"
          data-waslo-step-summary-name
        >
          <WasloInput
            :model-value="step.readBackEn ?? ''"
            :label="t(key('SUMMARY_NAME_EN'))"
            :max-length="MAX_READ_BACK_LABEL"
            data-waslo-step-read-back-en
            @update:model-value="value => setReadBackName('readBackEn', value)"
          />
          <WasloInput
            :model-value="step.readBackAr ?? ''"
            :label="t(key('SUMMARY_NAME_AR'))"
            :max-length="MAX_READ_BACK_LABEL"
            dir="auto"
            data-waslo-step-read-back-ar
            @update:model-value="value => setReadBackName('readBackAr', value)"
          />
        </div>
      </template>

      <!-- If they can't answer (P-10.25). -->
      <button
        type="button"
        class="flex items-center w-full gap-2 px-3 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
        :aria-expanded="open === 'stuck'"
        data-waslo-step-ifstuck-toggle
        @click="toggle('stuck')"
      >
        <span
          class="shrink-0 size-4 text-n-slate-11"
          :class="
            open === 'stuck'
              ? 'i-lucide-chevron-down'
              : 'i-lucide-chevron-right'
          "
          aria-hidden="true"
        />
        <span class="flex-1 min-w-0">{{ t(key('IFSTUCK')) }}</span>
        <span class="text-n-slate-11"
          ><bdi>{{ stuckName }}</bdi></span
        >
      </button>
      <div
        v-if="open === 'stuck'"
        class="flex flex-col gap-2 px-3 pb-2"
        data-waslo-step-ifstuck
      >
        <!-- One radio, in place, never a `<select>`: the same shape as the wiring screen's. -->
        <div
          role="radiogroup"
          :aria-label="t(key('IFSTUCK'))"
          class="flex flex-wrap gap-2"
        >
          <button
            v-for="action in STUCK_ACTIONS"
            :key="action"
            type="button"
            role="radio"
            :aria-checked="stuckAction === action ? 'true' : 'false'"
            :disabled="stuckDisabled(action)"
            class="px-3 py-1 text-sm rounded-lg outline outline-1 disabled:opacity-50"
            :class="
              stuckAction === action
                ? 'outline-n-blue-8 bg-n-blue-2 text-n-blue-11'
                : 'outline-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            :data-waslo-step-ifstuck-action="action"
            @click="setStuck(action)"
          >
            {{ t(key(`IFSTUCK_${action.toUpperCase()}`)) }}
          </button>
        </div>
        <!-- F1.3: the owner's "Optional". Refused where the owner's system is sent the answer (T-16). -->
        <label
          class="flex items-start gap-2 text-sm cursor-pointer text-n-slate-12"
          data-waslo-step-optional
        >
          <Checkbox
            class="mt-0.5 shrink-0"
            :model-value="isOptional"
            :disabled="fedBy !== '' && !isOptional"
            @update:model-value="setOptional"
          />
          <span class="flex flex-col gap-1">
            <span>{{ t(key('OPTIONAL')) }}</span>
            <span class="text-n-slate-11">{{ t(key('OPTIONAL_HINT')) }}</span>
          </span>
        </label>
        <p
          v-if="fedBy"
          class="mb-0 text-xs text-n-slate-11"
          data-waslo-step-skip-blocked
        >
          {{ t(key('SKIP_BLOCKED'), { connection: fedBy }) }}
        </p>
        <TaskStepLink
          v-if="stuckAction === 'goto'"
          :model-value="stuckNext"
          :label="t(key('IFSTUCK_GOTO_LABEL'))"
          :steps="stepChoices"
          :exclude="step.id"
          :allow-empty="false"
          data-waslo-step-ifstuck-next
          @update:model-value="setStuckNext"
        />
      </div>

      <template v-if="!isLive">
        <button
          type="button"
          class="flex items-center w-full gap-2 px-3 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
          :aria-expanded="open === 'advanced'"
          data-waslo-step-advanced-toggle
          @click="toggle('advanced')"
        >
          <span
            class="shrink-0 size-4 text-n-slate-11"
            :class="
              open === 'advanced'
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
            aria-hidden="true"
          />
          <span class="flex-1 min-w-0">{{ t(key('ADVANCED')) }}</span>
        </button>
        <div v-if="open === 'advanced'" class="px-3 pb-2">
          <WasloTextArea
            :model-value="step.instruction ?? ''"
            :label="t(key('INSTRUCTION'))"
            :hint="t(key('INSTRUCTION_HINT'))"
            :max-length="MAX_INSTRUCTION"
            min-height="4rem"
            data-waslo-step-instruction
            @update:model-value="value => patchStep({ instruction: value })"
          />
        </div>
      </template>
    </div>
  </div>
</template>
